import { isEmpty } from "lodash";
import PropTypes from "prop-types";
import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import styled, { useTheme } from "styled-components";

import themeGet from "@styled-system/theme-get";

import { Button, Icon, Popover } from "orcs-design-system";
import SelectComponents from "src/components/SelectComponents";
import { getSearchPath } from "src/consts/urlPaths";

import { trackEvent } from "src/services/segment";
import { EVENT_TRACKING } from "src/consts/eventTracking";
import { filterTypes } from "src/pages/SearchPage/SearchPage.config";
import { placeholderText, reactSelectStyles } from "./Search.config";
import DropdownIndicator from "./sub-components/DropDownIndicator";
import Menu from "./sub-components/Menu";
import MenuList from "./sub-components/MenuList";
import { useGlobalSearchContext } from "./GlobalSearchContext";

const SearchWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  [class$="-container"] {
    height: 100%;
  }
`;

const SearchFilter = styled(Button)`
  background-color: ${themeGet("colors.surfaceRaised")};
  color: ${themeGet("colors.subText")};
  border: none;
  border-left: 1px solid ${themeGet("colors.surfaceBorder")};
  border-radius: 0 ${themeGet("radii.2")} ${themeGet("radii.2")} 0;
  margin-right: 3px;
  font-size: ${themeGet("fontSizes.0")};
  height: 36px;
  margin-top: 1px;
  display: flex;
  align-items: center;
  font-weight: 600;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 2;
  &:hover,
  &:focus {
    box-shadow: none;
    background-color: ${themeGet("colors.surfaceRaised")};
    color: ${themeGet("colors.danger")};
    border: none;
    border-left: 1px solid ${themeGet("colors.surfaceBorder")};
    svg,
    path {
      color: ${themeGet("colors.danger")};
    }
  }
`;

const SearchFilterTeamName = styled.span`
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// TODO: Move this to ORCs when finalised
const Search = ({
  isMulti,
  isCloseOnSelect,
  isLoading,
  defaultMenuIsOpen = false,
  onOptionSelect,
  searchError,
  onDefaultScopeClearCallback,
  isForSearchPage = false,
  resultsFormattedData,
  loadingPeople,
  loadingPositions,
  loadingTeams,
  loadingTags,
  showPositions = false,
  workspace,
  emptyStateComponent,
  onLoadMore,
}) => {
  const [paddingRight, setPaddingRight] = useState(0);
  const [menuTop, setMenuTop] = useState(null);
  const theme = useTheme();
  const history = useHistory();
  const selectRef = useRef(null);
  const searchContainerRef = useRef(null);

  const overrideSelectStyles = useMemo(
    () => reactSelectStyles(theme, paddingRight, isLoading),
    [theme, paddingRight, isLoading]
  );

  const {
    defaultSearchScope,
    setDefaultSearchScope,
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
  } = useGlobalSearchContext();

  const filterRef = useRef(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (filterRef.current) {
      setPaddingRight(filterRef.current.clientWidth);
    } else {
      setPaddingRight(0);
    }
  });

  const updateMenuPosition = useCallback(() => {
    if (searchContainerRef.current) {
      const rect = searchContainerRef.current.getBoundingClientRect();
      setMenuTop(rect.bottom);
    }
  }, []);

  const onMenuOpen = useCallback(() => {
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
  }, [updateMenuPosition]);

  const onMenuClose = useCallback(() => {
    setMenuTop(null);
    window.removeEventListener("resize", updateMenuPosition);
    window.removeEventListener("scroll", updateMenuPosition, true);
  }, [updateMenuPosition]);

  useEffect(() => {
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [updateMenuPosition]);

  const onChange = useCallback(
    (value) => {
      if (onOptionSelect) {
        onOptionSelect(value);
      }
    },
    [onOptionSelect]
  );

  const onInputChange = useCallback(
    (query, { action }) => {
      if (action === "input-change") {
        // On search page refresh, avoid overwriting before URL sync: skip "" when searchTerm not yet set
        if (
          isForSearchPage &&
          (searchTerm === undefined || searchTerm === null) &&
          query === ""
        ) {
          return query;
        }
        setSearchTerm(query);

        // Restore focus after React renders on search page (setTimeout 0 defers to next tick)
        if (isForSearchPage) {
          setTimeout(() => {
            selectRef.current?.focus?.();
          }, 0);
        }
      }
      return query;
    },
    [isForSearchPage, setSearchTerm, searchTerm]
  );

  const onKeyDown = useCallback(
    (event) => {
      const { key } = event;

      // Keep focus when Enter is pressed on search page
      if (isForSearchPage && key === "Enter") {
        event.preventDefault();
        // setTimeout 0 runs after react-select's event handlers finish
        setTimeout(() => {
          selectRef.current?.focus?.();
        }, 0);
        return;
      }

      // Normal behavior for header search
      if (key === "Enter" && !isEmpty(searchTerm)) {
        const searchPath = getSearchPath(undefined, searchTerm);
        history.push(searchPath);
      }
    },
    [history, searchTerm, isForSearchPage]
  );

  const onDefaultScopeClear = useCallback(() => {
    setDefaultSearchScope(null);
    onDefaultScopeClearCallback(searchTerm);
    trackEvent(EVENT_TRACKING.SEARCH_FILTERED, {
      setting: "scope",
      cleared: true,
    });
  }, [setDefaultSearchScope, onDefaultScopeClearCallback, searchTerm]);

  const filterOption = useCallback(
    () => true,
    [] // Server-side global search already returns filtered matches.
  );

  const customComponents = useMemo(
    () => ({
      ...SelectComponents,
      DropdownIndicator,
      Menu,
      MenuList,
    }),
    []
  );

  const propsForCustomComponents = {
    menuTop,
    resultsFormattedData,
    loadingPeople,
    loadingPositions,
    loadingTeams,
    loadingTags,
    showPositions,
    debouncedSearchTerm,
    workspace,
    searchError,
    emptyStateComponent,
    onLoadMore,
    isForSearchPage,
    filterTypes,
  };

  return (
    <form data-testid="cp-search-form" style={{ height: "100%" }}>
      <SearchWrapper ref={searchContainerRef}>
        <Select
          ref={selectRef}
          name="global-search"
          inputId="global-search"
          cacheOptions={false}
          closeMenuOnSelect={isCloseOnSelect}
          closeMenuOnScroll={false}
          defaultMenuIsOpen={defaultMenuIsOpen}
          hideSelectedOptions={true}
          placeholder={placeholderText}
          onInputChange={onInputChange}
          onChange={onChange}
          isLoading={isLoading}
          options={[]}
          filterOption={filterOption}
          blurInputOnSelect={!isForSearchPage}
          openMenuOnFocus={!isForSearchPage}
          autoFocus={isForSearchPage}
          onKeyDown={onKeyDown}
          styles={overrideSelectStyles}
          aria-label={
            showPositions
              ? "Type to search for people, positions, teams or tags"
              : "Type to search for people, teams or tags"
          }
          role="searchbox"
          isMulti={isMulti}
          onMenuOpen={onMenuOpen}
          onMenuClose={onMenuClose}
          form="_none"
          inputValue={searchTerm || ""}
          components={customComponents}
          {...propsForCustomComponents}
        />
        {!isEmpty(defaultSearchScope) && (
          <SearchFilter
            ref={filterRef}
            small
            variant="dangerAlternate"
            onClick={onDefaultScopeClear}
            height="36px"
          >
            <Popover
              text="Click to remove filter"
              direction="bottom"
              textAlign="center"
              width="140px"
            >
              <SearchFilterTeamName>
                in: {defaultSearchScope.name}
              </SearchFilterTeamName>
              <Icon icon={["fas", "times"]} color="subText" ml="xs" />
            </Popover>
          </SearchFilter>
        )}
      </SearchWrapper>
    </form>
  );
};

Search.propTypes = {
  defaultMenuIsOpen: PropTypes.bool,
  isMulti: PropTypes.bool,
  isCloseOnSelect: PropTypes.bool,
  isLoading: PropTypes.bool,
  onOptionSelect: PropTypes.func,
  searchError: PropTypes.object,
  onDefaultScopeClearCallback: PropTypes.func,
  isForSearchPage: PropTypes.bool,
  resultsFormattedData: PropTypes.object,
  loadingPeople: PropTypes.bool,
  loadingPositions: PropTypes.bool,
  loadingTeams: PropTypes.bool,
  loadingTags: PropTypes.bool,
  showPositions: PropTypes.bool,
  workspace: PropTypes.object,
  emptyStateComponent: PropTypes.node,
  onLoadMore: PropTypes.func,
};

export default Search;
